import { UploadImageError, formatUploadLimit } from '@/utils/uploadErrors';
import { IMAGE_SIGNED_UPLOAD_MAX_BYTES, ANNOTATION_SERVER_FAILURE } from '@run-diffusion/shared';

/**
 * Why saving an annotation failed.
 *
 * Firebase owns full-resolution compositing and persistence. Legacy browser
 * reasons remain readable for existing reports; new saves use the shared server
 * taxonomy plus failures reaching the annotation service.
 */
export const ANNOTATION_SAVE_FAILURE = {
	...ANNOTATION_SERVER_FAILURE,
	/** The annotation service did not answer within the client timeout. */
	SAVE_TIMED_OUT: 'SAVE_TIMED_OUT',
	/** The editor opened without a resolvable source image URL. */
	NO_BACKGROUND_IMAGE: 'NO_BACKGROUND_IMAGE',
	/** The source image would not load for compositing (CORS, expired URL, blocked host). */
	BACKGROUND_IMAGE_LOAD_FAILED: 'BACKGROUND_IMAGE_LOAD_FAILED',
	/** The image loaded but without CORS approval, so the canvas cannot be exported. */
	BACKGROUND_IMAGE_BLOCKED: 'BACKGROUND_IMAGE_BLOCKED',
	/** Browser refused a 2D context, or the canvas exceeded its size limits. */
	CANVAS_UNAVAILABLE: 'CANVAS_UNAVAILABLE',
	/** toBlob produced nothing: usually a canvas past the browser's memory ceiling. */
	CANVAS_ENCODE_FAILED: 'CANVAS_ENCODE_FAILED',
	/** Neither load nor error fired within the budget. */
	COMPOSITE_TIMED_OUT: 'COMPOSITE_TIMED_OUT',
	/** The composited PNG exceeds what the upload service accepts. */
	IMAGE_TOO_LARGE: 'IMAGE_TOO_LARGE',
	/** Session gone or token unmintable. */
	NOT_SIGNED_IN: 'NOT_SIGNED_IN',
	/** The upload request never got a reply. */
	NETWORK_UNREACHABLE: 'NETWORK_UNREACHABLE',
	/** The upload service refused the image. */
	UPLOAD_REJECTED: 'UPLOAD_REJECTED',
	/** The upload service errored. */
	UPLOAD_SERVER_ERROR: 'UPLOAD_SERVER_ERROR',
	UNKNOWN: 'UNKNOWN',
} as const;

export type AnnotationSaveFailure = typeof ANNOTATION_SAVE_FAILURE[keyof typeof ANNOTATION_SAVE_FAILURE];

export class AnnotationSaveError extends Error {
	readonly reason: AnnotationSaveFailure;
	/** Server text safe to show the user, when the upload service sent one. */
	readonly trustedDetail: string;
	/** Correlation id for Cloud Logging, surfaced so support can trace a report. */
	readonly errorId: string | null;
	readonly cause?: unknown;

	constructor (
		reason: AnnotationSaveFailure,
		message: string,
		cause?: unknown,
		options?: { trustedDetail?: string, errorId?: string | null },
	) {
		super(message);
		// See UploadImageError: ES5 target drops the prototype chain when extending
		// a built-in, so `instanceof` needs restoring for the classification to hold.
		Object.setPrototypeOf(this, AnnotationSaveError.prototype);
		this.name = 'AnnotationSaveError';
		this.reason = reason;
		this.trustedDetail = options?.trustedDetail || '';
		this.errorId = options?.errorId || null;
		this.cause = cause;
	}
}

/**
 * A canvas holding a cross-origin image it was not granted access to throws on
 * export. Browsers word it differently, so match on the DOMException name first
 * and only fall back to the message text.
 */
const errorMessage = (err: unknown): string => {
	if (err && typeof err === 'object' && 'message' in err) return String((err as { message: unknown }).message);
	return String(err || 'Unknown error');
};

export const isCanvasTaintedError = (err: unknown): boolean => {
	if (!err) return false;
	if (typeof err === 'object' && 'name' in err && (err as { name: unknown }).name === 'SecurityError') return true;
	const message = errorMessage(err).toLowerCase();
	return message.includes('tainted') || message.includes('cross-origin data');
};

/** Wraps upload and callable failures into annotation-save reasons. */
export const toAnnotationSaveError = (err: unknown): AnnotationSaveError => {
	if (err instanceof AnnotationSaveError) return err;

	// httpsCallable rejects with this code once its timeout passes.
	if (err && typeof err === 'object' && 'code' in err && (err as { code: unknown }).code === 'deadline-exceeded') {
		return new AnnotationSaveError(ANNOTATION_SAVE_FAILURE.SAVE_TIMED_OUT, errorMessage(err), err);
	}

	if (err instanceof UploadImageError) {
		const reasonByKind: Record<string, AnnotationSaveFailure> = {
			NOT_SIGNED_IN: ANNOTATION_SAVE_FAILURE.NOT_SIGNED_IN,
			NETWORK_UNREACHABLE: ANNOTATION_SAVE_FAILURE.NETWORK_UNREACHABLE,
			TOO_LARGE: ANNOTATION_SAVE_FAILURE.IMAGE_TOO_LARGE,
			REJECTED: ANNOTATION_SAVE_FAILURE.UPLOAD_REJECTED,
			SERVER_ERROR: ANNOTATION_SAVE_FAILURE.UPLOAD_SERVER_ERROR,
		};
		return new AnnotationSaveError(
			reasonByKind[err.kind] || ANNOTATION_SAVE_FAILURE.UNKNOWN,
			err.message,
			err,
			{
				// Only our own function's JSON is fit to render. A proxy's HTML body
				// stays on err.message for the console and analytics detail.
				trustedDetail: err.isTrustedServerMessage ? err.serverMessage : '',
				errorId: err.errorId,
			},
		);
	}

	if (isCanvasTaintedError(err)) {
		return new AnnotationSaveError(
			ANNOTATION_SAVE_FAILURE.BACKGROUND_IMAGE_BLOCKED,
			errorMessage(err),
			err,
		);
	}

	return new AnnotationSaveError(
		ANNOTATION_SAVE_FAILURE.UNKNOWN,
		errorMessage(err),
		err,
	);
};

/**
 * The snackbar copy. Every message names what went wrong and what to try, so a
 * user can either self-serve or tell support something specific.
 */
export const getAnnotationSaveErrorMessage = (error: AnnotationSaveError): string => {
	// The annotation service writes its own messages for users.
	if (error.trustedDetail && (Object.values(ANNOTATION_SERVER_FAILURE) as string[]).includes(error.reason)) {
		return `Could not save annotations: ${error.trustedDetail}`;
	}
	switch (error.reason) {
		case ANNOTATION_SAVE_FAILURE.NO_BACKGROUND_IMAGE:
			return 'Could not save annotations: the source image is no longer loaded. Close the editor, reselect the image, and try again.';
		case ANNOTATION_SAVE_FAILURE.BACKGROUND_IMAGE_LOAD_FAILED:
			return 'Could not save annotations: the source image failed to load for saving. Refresh the page and try again. If you are on a company network, a proxy or VPN may be blocking image downloads.';
		case ANNOTATION_SAVE_FAILURE.BACKGROUND_IMAGE_BLOCKED:
			return 'Could not save annotations: your browser blocked access to the source image. Try a hard refresh (Ctrl+Shift+R, or Cmd+Shift+R on Mac), then annotate again.';
		case ANNOTATION_SAVE_FAILURE.CANVAS_UNAVAILABLE:
			return 'Could not save annotations: your browser could not prepare the image for saving. Close other tabs to free memory, then try again.';
		case ANNOTATION_SAVE_FAILURE.CANVAS_ENCODE_FAILED:
			return 'Could not save annotations: your browser ran out of memory while rendering the image. Close other tabs and try again, or annotate a smaller image.';
		case ANNOTATION_SAVE_FAILURE.COMPOSITE_TIMED_OUT:
			// The source image is already on screen by this point, so this timer
			// almost always covers local encoding rather than a download. Pointing
			// the user at their connection would send them to fix the wrong thing.
			return 'Could not save annotations: preparing the image took too long. Close other tabs to free memory and try again, or annotate a smaller image.';
		case ANNOTATION_SAVE_FAILURE.IMAGE_TOO_LARGE:
			return `Could not save annotations: the annotated image is larger than the ${formatUploadLimit(IMAGE_SIGNED_UPLOAD_MAX_BYTES)} upload limit. Try annotating a smaller version of this image.`;
		case ANNOTATION_SAVE_FAILURE.NOT_SIGNED_IN:
			return 'Could not save annotations: your session expired. Refresh the page to sign back in, then annotate again.';
		case ANNOTATION_SAVE_FAILURE.SAVE_TIMED_OUT:
			return 'Could not save annotations: saving took too long. Please try again.';
		case ANNOTATION_SAVE_FAILURE.SERVICE_UNAVAILABLE:
			return 'Could not save annotations: the annotation service had a problem. Please try again, and contact support if it keeps happening.';
		case ANNOTATION_SAVE_FAILURE.NETWORK_UNREACHABLE:
			return 'Could not save annotations: we could not reach the upload service. Check your connection and try again. If you are on a company network, a firewall or VPN may be blocking it.';
		case ANNOTATION_SAVE_FAILURE.UPLOAD_REJECTED:
			// error.message may be a proxy's HTML error page. Only render text the
			// upload service itself sent as JSON.
			return error.trustedDetail
				? `Could not save annotations: the upload was rejected (${error.trustedDetail}).`
				: 'Could not save annotations: the upload was rejected. Please try again, and contact support if it keeps happening.';
		case ANNOTATION_SAVE_FAILURE.UPLOAD_SERVER_ERROR:
			return error.errorId
				? `Could not save annotations: the upload service had a problem. Please try again, and contact support with reference ${error.errorId} if it keeps happening.`
				: 'Could not save annotations: the upload service had a problem. Please try again, and contact support if it keeps happening.';
		default:
			return 'Could not save annotations. Please try again, and contact support if it keeps happening.';
	}
};
